import { BriefCampaignInf } from "@shared/lib/types/briefCampains";
import { Flex, Modal, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { IUpdateTaskStartEndResponseItem } from "../lib/types";

const { Text } = Typography;

export interface FailedTaskRow {
  key: string;
  creatorName: string;
  creatorHandle?: string;
  deliverableName: string;
  taskName: string;
  reason: string;
  reasonType?: string;
}

interface ShowFailedTaskDatesModalParams {
  failedItems: IUpdateTaskStartEndResponseItem[];
  totalCount: number;
  influencers?: BriefCampaignInf[];
}

export function showFailedTaskDatesModal({
  failedItems,
  totalCount,
  influencers = [],
}: ShowFailedTaskDatesModalParams) {
  if (failedItems.length < 1) return;

  const successCount = totalCount - failedItems.length;

  const dataSource: FailedTaskRow[] = failedItems.map((item, index) => {
    const inf = influencers.find(
      (i) =>
        i._id === item.campaignInfluencerId ||
        i.influencerId === item.campaignInfluencerId ||
        i.briefInfluencerId === item.campaignInfluencerId
    );
    const creatorName =
      inf?.influencerName ||
      inf?.socialMedia?.searchV1?.fullName ||
      (inf?.socialMedia?.username ? `@${inf.socialMedia.username}` : "") ||
      item.campaignInfluencerId;

    const creatorHandle = inf?.socialMedia?.username
      ? `@${inf.socialMedia.username}`
      : undefined;

    const del =
      inf?.deliverables?.find((d) => d._id === item.deliverableConfigRefId) ||
      inf?.deliverables?.find((d) => d.tasks?.some((t) => t._id === item.taskId));
    const deliverableName = del?.deliverableType || item.deliverableConfigRefId;

    const task = del?.tasks?.find((t) => t._id === item.taskId);
    
    const taskName = task?.taskName || task?.type || item.taskId;

    return {
      key: `${item.campaignInfluencerId}_${item.deliverableConfigRefId}_${item.taskId}_${index}`,
      creatorName,
      creatorHandle: creatorHandle !== creatorName ? creatorHandle : undefined,
      deliverableName,
      taskName,
      reason: item.reason || item.reasonType || "Failed to update stage dates",
      reasonType: item.reasonType,
    };
  });

  const columns: ColumnsType<FailedTaskRow> = [
    {
      title: "Creator",
      dataIndex: "creatorName",
      key: "creatorName",
      render: (name: string, record) => (
        <Flex vertical>
          <Text strong style={{ fontSize: 13 }}>
            {name}
          </Text>
          {record.creatorHandle && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.creatorHandle}
            </Text>
          )}
        </Flex>
      ),
    },
    {
      title: "Deliverable",
      dataIndex: "deliverableName",
      key: "deliverableName",
      render: (val: string) => <Tag color="blue">{val}</Tag>,
    },
    {
      title: "Stage / Task",
      dataIndex: "taskName",
      key: "taskName",
      render: (val: string) => <Tag>{val}</Tag>,
    },
    {
      title: "Failure Reason",
      dataIndex: "reason",
      key: "reason",
      render: (reason: string, record) => (
        <Flex vertical gap={2}>
          <Text type="danger" style={{ fontSize: 12 }}>
            {reason}
          </Text>
          {record.reasonType && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              ({record.reasonType})
            </Text>
          )}
        </Flex>
      ),
    },
  ];

  return Modal.error({
    title: "Stage Dates Update — Failed Items",
    width: 720,
    okButtonProps: {
      type: "primary",
      style: { background: "#33312F" },
    },
    content: (
      <Flex vertical gap={12} style={{ marginTop: 12 }}>
        <Flex gap={8} wrap="wrap">
          {successCount > 0 && (
            <Tag color="success">{successCount} Succeeded</Tag>
          )}
          <Tag color="error">{failedItems.length} Failed</Tag>
        </Flex>
        <Table<FailedTaskRow>
          size="small"
          columns={columns}
          dataSource={dataSource}
          pagination={
            dataSource.length > 5
              ? { pageSize: 5, showSizeChanger: false }
              : false
          }
          scroll={{ x: "max-content", y: 280 }}
        />
      </Flex>
    ),
  });
}
